import type { CSSProperties, ReactNode } from "react"

// ── ActionPill (CON-223) ───────────────────────────────────
// The ONE row-action button: a rounded-full pill, 12px
// label, py-[5px] px-3, shared by every Today row verb
// (Do it / Snooze / Call) and every suggestion verb
// (Accept / Edit / Dismiss) across SuggestionCard and the
// Today templates. Two variants:
//
//   - "solid" — the row's primary verb. Caller paints
//     `bg`/`fg` (charcoal for Do it, amber for Accept —
//     palette-driven on themed surfaces).
//   - "ghost" — secondary verbs. Hairline `border` +
//     `fg` text + `hoverBg` wash, so the pill reads on
//     white cards and cream/dark cards alike.
//
// The `after:` pseudo pads the touch target toward 44px
// without changing visuals. Expansion is VERTICAL-ONLY
// (`inset-x-0 -inset-y-2`): height is the deficient touch
// dimension, and a horizontal expansion would overlap the
// adjacent pill across the actions-row gap, letting a tap
// near one pill's edge fire its neighbor.

const BASE_CLASSES =
  "relative flex items-center gap-1 rounded-full py-[5px] px-3 " +
  "text-[12px] leading-4 whitespace-nowrap shrink-0 cursor-pointer " +
  "focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-brand disabled:opacity-60 " +
  "disabled:cursor-not-allowed " +
  "after:absolute after:inset-x-0 after:-inset-y-2 " +
  "after:content-[''] after:rounded-full"

export interface ActionPillProps {
  variant: "solid" | "ghost"
  label: string
  // Optional leading glyph (PillCheckGlyph / PillXGlyph /
  // a phone icon, …).
  icon?: ReactNode
  // solid: pill background. ghost: ignored.
  bg?: string
  // Label (and caller-matched glyph) color.
  fg: string
  // ghost: hairline border color.
  border?: string
  // ghost: hover wash.
  hoverBg?: string
  onClick?: () => void
  disabled?: boolean
  testId?: string
  ariaLabel?: string
  title?: string
}

export function ActionPill({
  variant,
  label,
  icon,
  bg,
  fg,
  border,
  hoverBg,
  onClick,
  disabled,
  testId,
  ariaLabel,
  title,
}: ActionPillProps) {
  const solid = variant === "solid"
  const className = solid
    ? `${BASE_CLASSES} font-semibold`
    : `${BASE_CLASSES} font-medium border border-solid ` +
      "hover:[background-color:var(--pill-hover)]"
  const style: CSSProperties = solid
    ? { backgroundColor: bg, color: fg }
    : ({
        color: fg,
        borderColor: border,
        ["--pill-hover" as string]: hoverBg,
      } as CSSProperties)

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      className={className}
      style={style}
    >
      {icon}
      {label}
    </button>
  )
}

// 10px check — Accept's glyph.
export function PillCheckGlyph({ color }: { color: string }) {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3 8.5L6.5 12L13 4"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// 10px ✕ — Dismiss's glyph.
export function PillXGlyph({ color }: { color: string }) {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
